/**
 * HttpCacheEntry — the conditional-GET state for one server resource: the
 * validators the server last sent (ETag / Last-Modified) and, for a page of a
 * paginated collection, a manifest of what that page held so a 304 still says
 * what is on it.
 *
 * Rows are scoped by organization: a user who belongs to several organizations
 * refreshes the same collections in each, and one organization's validator must
 * never answer for another's.
 */
/** Dexie store definition; lives here (not in the service) so db.ts can import it without a cycle. */
export const HTTP_CACHE_SCHEMA = 'key, [organization_id+collection], updated_at';

/** What a page of a paginated collection held when its validator was recorded. */
export interface PageManifest {
  ids: Array<number | string>;
  /** The collection's page count as that response reported it. */
  totalPages: number;
  /** The collection's record count as that response reported it; null when the server sent none. */
  totalCount: number | null;
  /** The server's page size, as reported (`per_page`) or as measured from a full page of a multi-page walk. */
  pageSize?: number;
}

export interface HttpCacheEntry {
  /** `${organization_id}:${name}` — unique across organizations. */
  key: string;
  organization_id: number;
  /**
   * Entries forgotten together, e.g. `documents_42` for every page of that
   * client's documents. A paged collection sweeps everything in its collection
   * that is not one of its current pages, so a single resource's name must never
   * equal a paged collection's cacheKey.
   */
  collection: string;
  /** The caller's resource name, e.g. `documents_42_page_2`. */
  name: string;
  etag: string | null;
  last_modified: string | null;
  manifest?: PageManifest;
  updated_at: string;
}
